import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Divider, IconButton, Typography } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import * as React from 'react';
import { useModalContext } from './useModalContext';

interface PrimaryAction {
  label: string;
  onClick?: () => void;
  form?: string;
  dangerzone?: boolean;
  loading?: boolean;
  disabled?: boolean;
  id?: string;
}

interface SecondaryAction {
  label: string;
  onClick?: () => void;
  id?: string;
}

interface ModalTemplateProps {
  id?: string;
  title: string;
  children: React.ReactNode;
  primaryAction?: PrimaryAction;
  secondaryAction?: SecondaryAction;
  footer?: React.ReactNode;
}

const ModalTemplate: React.FC<ModalTemplateProps> = ({
  id,
  title,
  children,
  primaryAction,
  secondaryAction,
  footer
}) => {
  const { closeModal } = useModalContext();
  const { translation } = useTranslation();

  const handleSecondaryClick = () => {
    if (secondaryAction?.onClick) {
      secondaryAction.onClick();
    } else {
      closeModal();
    }
  };

  const hasFooter = footer || primaryAction || secondaryAction;

  return (
    <Box
      id={id}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: 'inherit'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          flexShrink: 0
        }}
      >
        <Typography variant="h6">{title}</Typography>
        <IconButton id="modal-close-button" onClick={closeModal} aria-label={translation.close} edge="end">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 3, pb: 4 }}>{children}</Box>
      {hasFooter && (
        <>
          <Divider />
          {footer ? (
            <Box sx={{ px: 3, py: 2, flexShrink: 0 }}>{footer}</Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'end',
                gap: 2,
                px: 3,
                py: 2,
                flexShrink: 0
              }}
            >
              <Button id={secondaryAction?.id} variant="text" onClick={handleSecondaryClick}>
                {secondaryAction?.label ?? 'Cancel'}
              </Button>
              {primaryAction && (
                <Button
                  id={primaryAction.id}
                  variant="contained"
                  color={primaryAction.dangerzone ? 'error' : 'primary'}
                  type={primaryAction.form ? 'submit' : 'button'}
                  form={primaryAction.form}
                  onClick={primaryAction.onClick}
                  loading={primaryAction.loading ?? false}
                  disabled={primaryAction.disabled ?? false}
                >
                  {primaryAction.label}
                </Button>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ModalTemplate;
